import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { authAxios } from '../contexts/AuthContext';
import { MapPin, Truck, CreditCard, FileText, Check, Plus, Home, Briefcase, MoreHorizontal } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { useAuth } from '../contexts/AuthContext';
import { useCart } from '../contexts/CartContext';
import { usePincode } from '../contexts/PincodeContext';
import { t, getProductName } from '../lib/translations';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CheckoutPage() {
  const navigate = useNavigate();
  const { user, language, isAuthenticated, token } = useAuth();
  const { cart, fetchCart } = useCart();
  const { currentPincode, checkPincode } = usePincode();

  const [addresses, setAddresses] = useState([]);
  const [selectedAddressId, setSelectedAddressId] = useState(null);
  const [useNewAddress, setUseNewAddress] = useState(false);

  const [newAddressForm, setNewAddressForm] = useState({
    house_flat_no: '',
    building_apt_name: '',
    landmark: '',
    city: '',
    state: '',
    pincode: currentPincode || '247776',
    address_type: 'Home',
    is_primary: false
  });

  const [phone, setPhone]           = useState(user?.phone || '');
  const [notes, setNotes]           = useState('');
  const [pincodeValid, setPincodeValid] = useState(true);
  const [loading, setLoading]       = useState(false);
  const [addressesLoading, setAddressesLoading] = useState(true);
  const [orderPlaced, setOrderPlaced] = useState(false);
  const [orderId, setOrderId]       = useState(null);

  // Delivery charge settings
  const [deliveryCharge,    setDeliveryCharge]    = useState(0);
  const [freeDeliveryAbove, setFreeDeliveryAbove] = useState(0);
  const [settingsLoading,   setSettingsLoading]   = useState(true);

  // Computed delivery charge for current cart total
  const subtotal = cart.total ?? 0;
  const appliedDeliveryCharge = (() => {
    if (deliveryCharge <= 0) return 0;
    if (freeDeliveryAbove > 0 && subtotal >= freeDeliveryAbove) return 0;
    return deliveryCharge;
  })();
  const orderTotal = subtotal + appliedDeliveryCharge;

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login', { state: { from: { pathname: '/checkout' } } });
    } else {
      fetchAddresses();
    }
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    if (cart.items.length === 0 && !orderPlaced) {
      navigate('/products');
    }
  }, [cart.items.length, navigate, orderPlaced]);

  // Fetch delivery settings from the public endpoint
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const { data } = await authAxios.get(`${API}/settings`);
        setDeliveryCharge(data.delivery_charge ?? 0);
        setFreeDeliveryAbove(data.free_delivery_above ?? 0);
      } catch (err) {
        // silently fail — treat as free delivery
        console.error('Failed to fetch delivery settings:', err);
      } finally {
        setSettingsLoading(false);
      }
    };
    fetchSettings();
  }, []);

  const fetchAddresses = async () => {
    try {
      const response = await authAxios.get(`${API}/auth/addresses`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAddresses(response.data);
      const primaryAddress = response.data.find(addr => addr.is_primary);
      if (primaryAddress) {
        setSelectedAddressId(primaryAddress.id);
      } else if (response.data.length > 0) {
        setSelectedAddressId(response.data[0].id);
      } else {
        setUseNewAddress(true);
      }
    } catch (error) {
      console.error('Failed to fetch addresses:', error);
    } finally {
      setAddressesLoading(false);
    }
  };

  const getAddressTypeIcon = (type) => {
    switch (type) {
      case 'Home': return <Home className="w-4 h-4" />;
      case 'Work': return <Briefcase className="w-4 h-4" />;
      default:     return <MoreHorizontal className="w-4 h-4" />;
    }
  };

  const buildFullAddress = (addr) =>
    `${addr.house_flat_no}${addr.building_apt_name ? ', ' + addr.building_apt_name : ''}${addr.landmark ? ', Near ' + addr.landmark : ''}, ${addr.city}, ${addr.state} - ${addr.pincode}`;

  const handleNewAddressChange = (e) => {
    setNewAddressForm({ ...newAddressForm, [e.target.name]: e.target.value });
  };

  const handleNewPincodeChange = async (e) => {
    const pincode = e.target.value.replace(/\D/g, '');
    setNewAddressForm({ ...newAddressForm, pincode });
    if (pincode.length === 6) {
      const result = await checkPincode(pincode);
      setPincodeValid(result.available);
    } else {
      setPincodeValid(true);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    let deliveryAddress = '';
    let deliveryPincode = '';

    if (useNewAddress || addresses.length === 0) {
      if (!newAddressForm.house_flat_no || !newAddressForm.city || !newAddressForm.state || !newAddressForm.pincode) {
        toast.error(language === 'hi' ? 'कृपया सभी आवश्यक फ़ील्ड भरें' : 'Please fill all required fields');
        return;
      }
      if (!pincodeValid) {
        toast.error(language === 'hi' ? 'डिलीवरी इस पिनकोड पर उपलब्ध नहीं' : 'Delivery not available for this pincode');
        return;
      }
      deliveryAddress = buildFullAddress(newAddressForm);
      deliveryPincode = newAddressForm.pincode;
    } else {
      const selected = addresses.find(a => a.id === selectedAddressId);
      if (!selected) {
        toast.error(language === 'hi' ? 'कृपया एक पता चुनें' : 'Please select an address');
        return;
      }
      deliveryAddress = buildFullAddress(selected);
      deliveryPincode = selected.pincode;
      const result = await checkPincode(deliveryPincode);
      if (!result.available) {
        toast.error(language === 'hi' ? 'चुने हुए पते पर डिलीवरी उपलब्ध नहीं' : 'Delivery not available for selected address pincode');
        return;
      }
    }

    if (!phone) {
      toast.error(language === 'hi' ? 'कृपया फोन नंबर दर्ज करें' : 'Please enter phone number');
      return;
    }

    setLoading(true);
    try {
      if (useNewAddress || addresses.length === 0) {
        try {
          await authAxios.post(`${API}/auth/addresses`, newAddressForm, {
            headers: { Authorization: `Bearer ${token}` }
          });
          toast.success(language === 'hi' ? 'पता प्रोफ़ाइल में सेव किया गया!' : 'Address saved to your profile!');
        } catch (err) {
          console.error('Failed to save address:', err);
        }
      }

      const response = await authAxios.post(
        `${API}/orders`,
        { delivery_address: deliveryAddress, delivery_pincode: deliveryPincode, phone, notes },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setOrderId(response.data.id);
      setOrderPlaced(true);
      await fetchCart();
      toast.success(t('orderPlaced', language));
    } catch (error) {
      toast.error(error.response?.data?.detail || (language === 'hi' ? 'ऑर्डर देने में विफल' : 'Failed to place order'));
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (price) => `₹${Number(price).toFixed(2)}`;

  if (orderPlaced) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4" data-testid="order-success">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <Check className="w-10 h-10 text-green-600" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900 mb-2">{t('orderPlaced', language)}</h1>
          <p className="text-slate-500 mb-2">
            {language === 'hi' ? 'ऑर्डर आईडी:' : 'Order ID:'} <span className="font-mono text-sm">{orderId}</span>
          </p>
          <p className="text-slate-500 mb-6">
            {language === 'hi'
              ? 'आपका ऑर्डर सफलतापूर्वक दिया गया। आप डिलीवरी पर भुगतान करेंगे।'
              : 'Your order has been placed successfully. You will pay on delivery.'}
          </p>
          <div className="space-y-3">
            <Button onClick={() => navigate('/orders')} className="w-full bg-green-600 hover:bg-green-700" data-testid="view-orders-btn">
              {t('myOrders', language)}
            </Button>
            <Button variant="outline" onClick={() => navigate('/products')} className="w-full">
              {t('continueShopping', language)}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 py-8" data-testid="checkout-page">
      <div className="container-app">
        <h1 className="text-2xl font-bold text-slate-900 mb-6">{t('checkout', language)}</h1>

        <div className="grid lg:grid-cols-3 gap-8">

          {/* ── Left: Form ── */}
          <div className="lg:col-span-2">
            <form onSubmit={handleSubmit} className="space-y-6">

              {/* Delivery Address */}
              <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-100">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                      <MapPin className="w-5 h-5 text-green-600" />
                    </div>
                    <h2 className="text-lg font-semibold">{t('deliveryAddress', language)}</h2>
                  </div>
                  <Link to="/profile" className="text-sm text-green-600 hover:text-green-700 font-medium">
                    {language === 'hi' ? 'पते प्रबंधित करें' : 'Manage Addresses'}
                  </Link>
                </div>

                {addressesLoading ? (
                  <div className="py-4 text-center">
                    <div className="animate-spin w-6 h-6 border-4 border-green-600 border-t-transparent rounded-full mx-auto" />
                  </div>
                ) : (
                  <div className="space-y-4">
                    {/* Saved Addresses */}
                    {addresses.length > 0 && !useNewAddress && (
                      <div>
                        <Label className="mb-2 block">{language === 'hi' ? 'सहेजे गए पते' : 'Saved Addresses'}</Label>
                        <RadioGroup value={selectedAddressId} onValueChange={setSelectedAddressId} className="space-y-3">
                          {addresses.map((address) => (
                            <div key={address.id} className="flex items-start gap-3">
                              <RadioGroupItem value={address.id} id={address.id} className="mt-1" />
                              <Label htmlFor={address.id} className="flex-1 cursor-pointer">
                                <div className={`border rounded-lg p-4 ${selectedAddressId === address.id ? 'border-green-600 bg-green-50' : 'border-slate-200'}`}>
                                  <div className="flex items-center gap-2 mb-2">
                                    <div className="text-green-600">{getAddressTypeIcon(address.address_type)}</div>
                                    <span className="font-semibold">{address.address_type}</span>
                                    {address.is_primary && (
                                      <span className="text-xs bg-green-600 text-white px-2 py-0.5 rounded-full">
                                        {language === 'hi' ? 'मुख्य' : 'Primary'}
                                      </span>
                                    )}
                                  </div>
                                  <p className="text-sm text-slate-600 leading-relaxed">
                                    {address.house_flat_no}
                                    {address.building_apt_name && `, ${address.building_apt_name}`}
                                    {address.landmark && `, ${address.landmark}`}
                                    <br />
                                    {address.city}, {address.state} - {address.pincode}
                                  </p>
                                </div>
                              </Label>
                            </div>
                          ))}
                        </RadioGroup>
                        <Button type="button" variant="outline" onClick={() => setUseNewAddress(true)} className="w-full mt-3">
                          <Plus className="w-4 h-4 mr-2" />
                          {language === 'hi' ? 'नया पता उपयोग करें' : 'Use New Address'}
                        </Button>
                      </div>
                    )}

                    {/* New Address Form */}
                    {(useNewAddress || addresses.length === 0) && (
                      <div className="space-y-4">
                        {addresses.length > 0 && (
                          <Button
                            type="button"
                            variant="ghost"
                            onClick={() => {
                              setUseNewAddress(false);
                              const primary = addresses.find(a => a.is_primary) || addresses[0];
                              setSelectedAddressId(primary.id);
                            }}
                            className="mb-2"
                          >
                            ← {language === 'hi' ? 'सहेजे गए पतों पर वापस जाएं' : 'Back to saved addresses'}
                          </Button>
                        )}
                        <p className="text-sm text-green-600 font-medium">
                          {language === 'hi' ? '✓ यह पता आपकी प्रोफ़ाइल में सेव हो जाएगा' : '✓ This address will be saved to your profile'}
                        </p>
                        <div>
                          <Label htmlFor="house_flat_no">{language === 'hi' ? 'घर/फ्लैट नंबर *' : 'House/Flat No. *'}</Label>
                          <Input id="house_flat_no" name="house_flat_no" value={newAddressForm.house_flat_no} onChange={handleNewAddressChange} required className="mt-1" placeholder="e.g., 123, Flat 4B" />
                        </div>
                        <div>
                          <Label htmlFor="building_apt_name">{language === 'hi' ? 'बिल्डिंग/अपार्टमेंट का नाम' : 'Building/Apartment Name'}</Label>
                          <Input id="building_apt_name" name="building_apt_name" value={newAddressForm.building_apt_name} onChange={handleNewAddressChange} className="mt-1" placeholder="e.g., Green View Apartments" />
                        </div>
                        <div>
                          <Label htmlFor="landmark">{language === 'hi' ? 'लैंडमार्क' : 'Landmark'}</Label>
                          <Input id="landmark" name="landmark" value={newAddressForm.landmark} onChange={handleNewAddressChange} className="mt-1" placeholder="e.g., Near City Hospital" />
                        </div>
                        <div className="grid sm:grid-cols-2 gap-4">
                          <div>
                            <Label htmlFor="city">{language === 'hi' ? 'शहर *' : 'City *'}</Label>
                            <Input id="city" name="city" value={newAddressForm.city} onChange={handleNewAddressChange} required className="mt-1" />
                          </div>
                          <div>
                            <Label htmlFor="state">{language === 'hi' ? 'राज्य *' : 'State *'}</Label>
                            <Input id="state" name="state" value={newAddressForm.state} onChange={handleNewAddressChange} required className="mt-1" />
                          </div>
                        </div>
                        <div className="grid sm:grid-cols-2 gap-4">
                          <div>
                            <Label htmlFor="new_pincode">{language === 'hi' ? 'पिनकोड *' : 'Pincode *'}</Label>
                            <Input id="new_pincode" name="pincode" value={newAddressForm.pincode} onChange={handleNewPincodeChange} required maxLength={6} className={`mt-1 ${!pincodeValid ? 'border-red-500' : ''}`} />
                            {!pincodeValid && <p className="text-red-500 text-sm mt-1">{t('deliveryNotAvailable', language)}</p>}
                          </div>
                          <div>
                            <Label htmlFor="address_type">{language === 'hi' ? 'पते का प्रकार' : 'Address Type'}</Label>
                            <Select value={newAddressForm.address_type} onValueChange={(value) => setNewAddressForm({ ...newAddressForm, address_type: value })}>
                              <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="Home">{language === 'hi' ? 'घर' : 'Home'}</SelectItem>
                                <SelectItem value="Work">{language === 'hi' ? 'कार्यालय' : 'Work'}</SelectItem>
                                <SelectItem value="Other">{language === 'hi' ? 'अन्य' : 'Other'}</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <input type="checkbox" id="is_primary" checked={newAddressForm.is_primary}
                            onChange={(e) => setNewAddressForm({ ...newAddressForm, is_primary: e.target.checked })}
                            className="w-4 h-4 text-green-600 rounded focus:ring-green-500" />
                          <Label htmlFor="is_primary" className="cursor-pointer">
                            {language === 'hi' ? 'मुख्य पते के रूप में सेट करें' : 'Set as primary address'}
                          </Label>
                        </div>
                      </div>
                    )}

                    {/* Phone */}
                    <div>
                      <Label htmlFor="phone">{t('phone', language)} *</Label>
                      <Input id="phone" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)}
                        placeholder="9876543210" required className="mt-1" data-testid="phone-input" />
                    </div>
                  </div>
                )}
              </div>

              {/* Order Notes */}
              <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-100">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                    <FileText className="w-5 h-5 text-green-600" />
                  </div>
                  <h2 className="text-lg font-semibold">{t('notes', language)}</h2>
                </div>
                <Textarea value={notes} onChange={(e) => setNotes(e.target.value)}
                  placeholder={language === 'hi' ? 'कोई विशेष निर्देश...' : 'Any special instructions...'} rows={2} data-testid="notes-input" />
              </div>

              {/* Payment Method */}
              <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-100">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                    <CreditCard className="w-5 h-5 text-green-600" />
                  </div>
                  <h2 className="text-lg font-semibold">
                    {language === 'hi' ? 'भुगतान विधि' : 'Payment Method'}
                  </h2>
                </div>
                <div className="flex items-center gap-3 p-4 bg-green-50 rounded-lg border-2 border-green-500">
                  <Truck className="w-6 h-6 text-green-600" />
                  <div>
                    <p className="font-medium text-green-700">
                      {language === 'hi' ? 'कैश ऑन डिलीवरी (COD)' : 'Cash on Delivery (COD)'}
                    </p>
                    <p className="text-sm text-green-600">
                      {language === 'hi' ? 'डिलीवरी पर भुगतान करें' : 'Pay when you receive'}
                    </p>
                  </div>
                </div>
              </div>

              <Button type="submit" className="w-full bg-green-600 hover:bg-green-700 h-14 text-lg" disabled={loading} data-testid="place-order-btn">
                {loading ? (
                  <div className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full" />
                ) : (
                  <>
                    {t('placeOrder', language)}
                    {!settingsLoading && (
                      <span className="ml-2 opacity-80">
                        — {formatPrice(orderTotal)}
                      </span>
                    )}
                  </>
                )}
              </Button>
            </form>
          </div>

          {/* ── Right: Order Summary ── */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-100 sticky top-24">
              <h2 className="text-lg font-semibold mb-4">
                {language === 'hi' ? 'ऑर्डर सारांश' : 'Order Summary'}
              </h2>

              <div className="space-y-3 max-h-64 overflow-y-auto">
                {cart.items.map((item) => (
                  <div key={item.product_id + (item.variant_id || '')} className="flex gap-3">
                    <img src={item.product?.image} alt={item.product ? getProductName(item.product, language) : ''}
                      className="w-12 h-12 object-cover rounded-lg" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">
                        {item.product ? getProductName(item.product, language) : ''}
                      </p>
                      <p className="text-xs text-slate-500">
                        {item.quantity} x {formatPrice(item.product?.discount_price || item.product?.price || 0)}
                      </p>
                    </div>
                    <p className="text-sm font-medium text-slate-700 shrink-0">
                      {formatPrice((item.product?.discount_price || item.product?.price || 0) * item.quantity)}
                    </p>
                  </div>
                ))}
              </div>

              <div className="border-t border-slate-100 mt-4 pt-4 space-y-2 text-sm">
                {/* Subtotal */}
                <div className="flex justify-between text-slate-600">
                  <span>{language === 'hi' ? 'उप-योग' : 'Subtotal'}</span>
                  <span>{formatPrice(subtotal)}</span>
                </div>

                {/* Delivery charge row */}
                {settingsLoading ? (
                  <div className="flex justify-between text-slate-400">
                    <span>{language === 'hi' ? 'डिलीवरी' : 'Delivery'}</span>
                    <span className="animate-pulse">...</span>
                  </div>
                ) : (
                  <>
                    <div className="flex justify-between">
                      <span className="text-slate-600">{language === 'hi' ? 'डिलीवरी शुल्क' : 'Delivery Charge'}</span>
                      {appliedDeliveryCharge > 0 ? (
                        <span className="font-medium text-slate-800">{formatPrice(appliedDeliveryCharge)}</span>
                      ) : (
                        <span className="text-green-600 font-medium">
                          {language === 'hi' ? 'मुफ्त' : 'FREE'}
                        </span>
                      )}
                    </div>

                    {/* Free delivery progress hint */}
                    {deliveryCharge > 0 && freeDeliveryAbove > 0 && appliedDeliveryCharge > 0 && (
                      <div className="mt-2">
                        <div className="text-xs text-slate-500 mb-1">
                          {language === 'hi'
                            ? `₹${(freeDeliveryAbove - subtotal).toFixed(0)} और जोड़ें — मुफ्त डिलीवरी पाएं!`
                            : `Add ₹${(freeDeliveryAbove - subtotal).toFixed(0)} more for FREE delivery!`}
                        </div>
                        <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
                          <div
                            className="h-full bg-green-500 rounded-full transition-all duration-300"
                            style={{ width: `${Math.min((subtotal / freeDeliveryAbove) * 100, 100)}%` }}
                          />
                        </div>
                      </div>
                    )}

                    {/* Already earned free delivery */}
                    {deliveryCharge > 0 && freeDeliveryAbove > 0 && appliedDeliveryCharge === 0 && subtotal >= freeDeliveryAbove && (
                      <p className="text-xs text-green-600 font-medium">
                        🎉 {language === 'hi' ? 'आपने मुफ्त डिलीवरी अर्जित की!' : 'You earned free delivery!'}
                      </p>
                    )}
                  </>
                )}

                {/* Grand total */}
                <div className="flex justify-between text-lg font-bold pt-2 border-t border-slate-100">
                  <span>{t('total', language)}</span>
                  <span className="text-green-600" data-testid="checkout-total">
                    {formatPrice(orderTotal)}
                  </span>
                </div>

                {/* COD note */}
                <p className="text-xs text-slate-400 text-center pt-1">
                  {language === 'hi' ? 'डिलीवरी पर नकद भुगतान' : 'Pay cash on delivery'}
                </p>
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}
